/**
 * BannerDiagnostic — طبقة تشخيص بصريّة فوق بطاقة البانر.
 * تُفعَّل عبر localStorage['lecmo-banner-diag'] = '1' (يضبطه /admin/banners).
 *
 * تعرض:
 *  - أبعاد العنصر الفعليّة (عرض × ارتفاع) والنسبة الفعليّة المحسوبة.
 *  - النسبة المطلوبة عبر CSS (من aspectRatio أو className).
 *  - سلسلة الأجداد حتى 6 مستويات: tag + أهمّ class عرض/ارتفاع + العرض الفعليّ.
 *  - تنبيه أحمر إن كانت النسبة الفعليّة أعمدَ من المطلوب (height > width).
 */
import * as React from "react";

export const BANNER_DIAG_KEY = "lecmo-banner-diag";

export function useBannerDiagEnabled(): boolean {
  const [on, setOn] = React.useState(false);
  React.useEffect(() => {
    const read = () => {
      try {
        setOn(localStorage.getItem(BANNER_DIAG_KEY) === "1");
      } catch {
        setOn(false);
      }
    };
    read();
    const handler = (e: StorageEvent) => {
      if (e.key === BANNER_DIAG_KEY) read();
    };
    window.addEventListener("storage", handler);
    const interval = window.setInterval(read, 1500);
    return () => {
      window.removeEventListener("storage", handler);
      window.clearInterval(interval);
    };
  }, []);
  return on;
}

interface AncestorInfo {
  tag: string;
  cls: string;
  width: number;
  height: number;
}

function summarizeClass(cls: string): string {
  if (!cls) return "—";
  const parts = cls.split(/\s+/).filter((c) =>
    /^(w-|h-|min-w|max-w|aspect|flex-|grid|basis-|shrink|grow|px-|py-|p-)/.test(c)
  );
  const out = parts.slice(0, 4).join(" ");
  return out || cls.slice(0, 40);
}

export function BannerDiagnosticOverlay({
  targetRef,
  label,
}: {
  targetRef: React.RefObject<HTMLElement | null>;
  label: string;
}) {
  const enabled = useBannerDiagEnabled();
  const [info, setInfo] = React.useState<{
    w: number;
    h: number;
    expected: string;
    ancestors: AncestorInfo[];
  } | null>(null);

  React.useEffect(() => {
    if (!enabled) return;
    const el = targetRef.current;
    if (!el) return;

    const measure = () => {
      const r = el.getBoundingClientRect();
      const cs = getComputedStyle(el);
      const expected = cs.aspectRatio && cs.aspectRatio !== "auto" ? cs.aspectRatio : "—";
      const ancestors: AncestorInfo[] = [];
      let node: HTMLElement | null = el.parentElement;
      let depth = 0;
      while (node && depth < 6) {
        const br = node.getBoundingClientRect();
        ancestors.push({
          tag: node.tagName.toLowerCase(),
          cls: summarizeClass(node.className || ""),
          width: Math.round(br.width),
          height: Math.round(br.height),
        });
        node = node.parentElement;
        depth++;
      }
      setInfo({
        w: Math.round(r.width),
        h: Math.round(r.height),
        expected,
        ancestors,
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    let n: HTMLElement | null = el.parentElement;
    let d = 0;
    while (n && d < 6) {
      ro.observe(n);
      n = n.parentElement;
      d++;
    }
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [enabled, targetRef]);

  if (!enabled || !info) return null;

  const actualRatio = info.h > 0 ? info.w / info.h : 0;
  const isVertical = info.h > info.w;

  return (
    <div
      dir="ltr"
      className="pointer-events-none absolute inset-0 z-50 flex flex-col justify-between p-2 font-mono text-[10px] leading-tight"
      style={{
        background: isVertical
          ? "linear-gradient(rgba(220,38,38,0.18), rgba(220,38,38,0.05))"
          : "linear-gradient(rgba(16,185,129,0.10), transparent)",
        outline: isVertical ? "2px dashed #dc2626" : "2px dashed #10b981",
      }}
    >
      <div className="pointer-events-auto self-start rounded bg-black/85 px-2 py-1 text-white shadow">
        <div className="font-bold">{label}</div>
        <div>
          actual: {info.w}×{info.h}px (ratio {actualRatio.toFixed(2)})
        </div>
        <div>expected aspect-ratio: {info.expected}</div>
        {isVertical && (
          <div className="text-red-300">⚠ vertical — height &gt; width</div>
        )}
      </div>
      <div className="pointer-events-auto max-h-[55%] self-stretch overflow-auto rounded bg-black/85 p-1.5 text-white shadow">
        <div className="mb-1 font-bold text-yellow-300">ancestor chain (closest → root)</div>
        {info.ancestors.map((a, i) => {
          const tooNarrow = a.width > 0 && a.width < info.h;
          return (
            <div
              key={i}
              className={tooNarrow ? "text-red-300" : "text-white"}
            >
              {i}. &lt;{a.tag}&gt; {a.width}×{a.height} · {a.cls}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** زرّ تبديل التشخيص — يُركَّب في /admin/banners. */
export function BannerDiagnosticToggle() {
  const [on, setOn] = React.useState(false);
  React.useEffect(() => {
    try {
      setOn(localStorage.getItem(BANNER_DIAG_KEY) === "1");
    } catch {
      /* noop */
    }
  }, []);
  const toggle = () => {
    const next = !on;
    setOn(next);
    try {
      localStorage.setItem(BANNER_DIAG_KEY, next ? "1" : "0");
      window.dispatchEvent(
        new StorageEvent("storage", { key: BANNER_DIAG_KEY, newValue: next ? "1" : "0" })
      );
    } catch {
      /* noop */
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      className={`rounded-none border px-3 py-1.5 text-xs font-medium transition-colors ${
        on
          ? "border-red-500 bg-red-500/10 text-red-600"
          : "border-border bg-background text-muted-foreground hover:bg-muted"
      }`}
      title="يُظهر طبقة تشخيص فوق بطاقات البانر تكشف من يفرض النسبة العمودية"
    >
      {on ? "🔴 تشخيص النسبة: مُفعَّل" : "🩺 شغّل تشخيص النسبة"}
    </button>
  );
}
